import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import LogoImage from "assets/images/Logo blue.png";
import sampleJson from "../SurveyV4/sample.json";
import planData from "./plan.json";
import mockRecommendations from "./recommendations.json";
import {
  SG_GET_CHAT_GPT_SUMMARY,
  CLEAR_CHAT_GPT_SUMMARY,
  SG_AP_BOOTSTRAP,

} from "constants/actions";

import {
  Container,
  Content,
  TopBanner,
  BottomBar,
  ButtonWrapper,
  NextButton,
  BackButton,
  Progress,
  ProgressWrapper,
  AnimatedCard,
  ChatBarContainer,
  ChatBarInner,
  ChatInputField,
  SendBtn,
  NextButtonWrapper,
  GrowButtonWrapper,
  NoraDrawer,
  NoraOverlay,
  NoraHeader,
  NoraHeaderLeft,
  NoraTitle,
  NoraSubtitle,
  NoraClose,
  NoraMessages,
  NoraInputBar,
  NoraTextField,
  NoraSendButton,
  NoraMessageRow,
  NoraMessageBubble,
  NoraTimestamp,
  TypingDot,
  LoadingSpinner,
  SkeletonBar,
  RecommendationReveal,
} from "./styles";
import ActionStep from "./ActionStep";
import Introduction from "./Introduction";
import ReviewConfirm from "./ReviewConfirm";
import SubmissionComplete from "./SubmissionComplete";
import PreSubmitInfo from "./PreSubmitInfo";
import Language from "../AuditV2/Language";
import Loading from "./Components/Loading";
import InvalidToken from "./Components/InvalidToken";
import Completed from "./Components/Completed";

// Selectors for token-based bootstrap data
const useActionPlanningState = () =>
  useSelector(
    (state) => state.actionPlanning || state.ap || state.action_plan || {},
    shallowEqual
  );

const epicIn = keyframes`
  0% { opacity: 0; transform: translateY(20px) scale(0.98); }
  60% { opacity: 1; transform: translateY(0) scale(1.02); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const EpicTitle = styled.h1`
  font-family: 'Geist Variable', 'Geist', sans-serif;
  font-weight: 500;
  font-size: clamp(30px, 6vw, 40px);
  text-align: left;
  line-height: 1.05;
  margin: 0;
  letter-spacing: 0.5px;
  color: oklch(.145 0 0);
  animation: ${epicIn} 800ms ease-out both;
`;

const QuestionText = styled.h2`
  font-family: 'Geist Variable', 'Geist', sans-serif;
  font-weight: 500;
  font-size: clamp(22px, 3.5vw, 32px);
  text-align: left;
  margin: 0;
  color: oklch(.145 0 0);
  letter-spacing: 0.2px;
`;

const CenteredBlock = styled.div`
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
`;

const Logo = styled.img`
  height: 30px;
  position: absolute;
  top: 20px;
  left: 20px;
`;

const BackgroundLayer = styled.div`
  position: absolute;
  inset: 0;
  top: -96px;
  height: 320px;
  pointer-events: none;
  z-index: 0;
`;

const floatY1 = keyframes`
  0% { transform: translateY(-6px); opacity: 0.9; }
  100% { transform: translateY(6px); opacity: 1; }
`;

const floatY2 = keyframes`
  0% { transform: translateY(8px); opacity: 0.9; }
  100% { transform: translateY(-8px); opacity: 1; }
`;

const BlobLeft = styled.div`
  position: absolute;
  left: -8%;
  top: 0;
  width: 320px;
  height: 320px;
  border-radius: 9999px;
  background: rgba(99, 102, 241, 0.2); /* indigo-400/20 */
  filter: blur(60px);
  animation: ${floatY1} 8s ease-in-out infinite alternate;
`;

const BlobRight = styled.div`
  position: absolute;
  right: -6%;
  top: 24px;
  width: 288px;
  height: 288px;
  border-radius: 9999px;
  background: rgba(34, 211, 238, 0.2); /* cyan-400/20 */
  filter: blur(60px);
  animation: ${floatY2} 10s ease-in-out infinite alternate;
`;

const Foreground = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Action plan UI styles
const PlanHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubTitle = styled.p`
  margin: 6px 0 0 0;
  color: #4b5563;
  font-size: 15px;
`;

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
`;

const ActionCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  background: #ffffff;
`;

const ActionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: oklch(.145 0 0);
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #374151;
`;

const DateInput = styled.input`
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 10px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
`;

export default function ActionPlan() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token: pathToken } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const queryToken = urlParams.get("token");
  const token = pathToken || queryToken || "";
  const { chatGptState } = useSelector(
    (state) => ({ chatGptState: state.chatgpt }),
    shallowEqual
  );
  const [isFadingOut, setIsFadingOut] = useState(false);
  const apState = useActionPlanningState();
  const {
    loading: apLoading,
    loadingBootstrap: apLoadingBootstrap,
    error: apError,
    events: apEvents = [],
    actions: apActions = [],
    activeEvent: apActiveEvent = null,
    requiredActionsCount: apRequiredActionsCount = 3,
    invalidToken: apInvalidToken = false,
  } = apState;
  const questions = [
    "What’s your top priority today?",
    "Where do you feel most confident right now?",
    "What would make the biggest impact this week?",
    "What’s one thing you want to improve?",
  ];
  const [currentIndex, setCurrentIndex] = useState(-2); // -2 = intro, 0..n-1 = questions, n.. = review
  const [mode, setMode] = useState('self'); // default to self-guided
  const [showFinalReview, setShowFinalReview] = useState(false);
  const [showSubmissionComplete, setShowSubmissionComplete] = useState(false);
  const [showChatBar, setShowChatBar] = useState(false);
  const [shrinkNext, setShrinkNext] = useState(false);
  const [chatIsLeaving, setChatIsLeaving] = useState(false);
  const [showFinishBtn, setShowFinishBtn] = useState(false);
  const [chatDraft, setChatDraft] = useState("");
  const [responses, setResponses] = useState({});
  const [noraOpen, setNoraOpen] = useState(false);
  const [noraMessages, setNoraMessages] = useState([
    { id: 'm0', role: 'assistant', content: "Hi, I’m Nora — your assistant for building clear, actionable steps. I can help you refine titles and descriptions, suggest milestones and due dates, make goals specific and time-bound, and brainstorm owners. I can see the action you’re currently editing to keep advice contextual. I don’t access anything outside of this page." , timestamp: new Date() }
  ]);
  const [noraDraft, setNoraDraft] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [showAIGoBackConfirm, setShowAIGoBackConfirm] = useState(false);
  const [language, setLanguage] = useState('EN');
  const [introAcknowledged, setIntroAcknowledged] = useState(false);
  const [showPreSubmit, setShowPreSubmit] = useState(false);

  const [actions, setActions] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [explanationsByActionId, setExplanationsByActionId] = useState({});
  const [previousSuggestionsByActionId, setPreviousSuggestionsByActionId] = useState({});
  const [explanationOpenForId, setExplanationOpenForId] = useState(null);
  const [recommendationsByActionId, setRecommendationsByActionId] = useState({});
  const [selectedRecommendationIndexByActionId, setSelectedRecommendationIndexByActionId] = useState({});
  const tokenRef = useRef("");
  const loadingHideTimerRef = useRef(null);
  const [revealTokenByActionId, setRevealTokenByActionId] = useState({});
  const [errorByActionId, setErrorByActionId] = useState({});

  // Ensure a fresh start on each load: clear persisted actions
  useEffect(() => {
    try {
      localStorage.removeItem("actionPlan:v1");
    } catch (e) {
      // ignore
    }
  }, []);

  function createEmptyRows(n) {
    const count = Math.max(1, Number(n) || 3);
    return Array.from({ length: count }, (_, i) => ({
      id: `s${i + 1}`,
      title: "",
      description: "",
      date: "",
      owner: "",
      reviewed: false,
    }));
  }

  // Hydrate from Redux (token bootstrap) first; fall back to any saved local drafts
  useEffect(() => {
    // If we have actions from the API, map them into the local UI model
    if (Array.isArray(apActions) && apActions.length > 0) {
      setActions(apActions.map((a) => ({
        id: a.id,
        title: a.title || "",
        description: a.description || "",
        date: a.time_bound_deadline || a.date || "",
        owner: a.owner_manager || a.owner || "",
        reviewed: false,
      })));
      return;
    }
    // Otherwise, try to restore a local draft
    try {
      const raw = localStorage.getItem("actionPlan:v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.actions)) {
          setActions(parsed.actions.map((a) => ({
            id: a.id,
            title: a.title,
            description: a.description || "",
            date: a.date || "",
            owner: a.owner || "",
            reviewed: !!a.reviewed,
          })));
        }
      } else {
        // Initialize with requiredActionsCount (fallback 3)
        setActions(createEmptyRows(apRequiredActionsCount));
      }
    } catch (e) {
      // ignore
    }
  // Re-run when token bootstrap loads actions
  }, [apActions, apRequiredActionsCount]);

  // Dispatch bootstrap on mount when token is present
  useEffect(() => {
    if (!token) return;
    dispatch({ type: SG_AP_BOOTSTRAP, payload: { token, kind: "plan" } });
  }, [token, dispatch]);

  // Initialize language from URL param on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const langParam = params.get('language');
      if (!langParam) return;
      if (langParam === '0' || langParam.toUpperCase() === 'EN') {
        setLanguage('EN');
      } else if (langParam === '1' || langParam.toUpperCase() === 'FR') {
        setLanguage('FR');
      } else if (langParam === '2' || langParam.toUpperCase() === 'ES') {
        setLanguage('ES');
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("actionPlan:v1", JSON.stringify({ actions }));
    } catch (e) {
      // ignore
    }
  }, [actions]);

  const themeColors = {
    buttonColor: sampleJson?.themeSettings?.buttonColor || '#007bff',
    highlightText: sampleJson?.themeSettings?.highlightText || '#333',
    topBanner: sampleJson?.themeSettings?.topBanner || '#f8f9fa',
    secondaryButton: sampleJson?.themeSettings?.secondaryButton || '#6c757d',
    backgroundColor: sampleJson?.themeSettings?.backgroundColor || '#ffffff',
  };

  // Debug/testing flags via URL params
  const forceLoadingParam = String((urlParams && urlParams.get('loading')) || '').toLowerCase();
  const forceLoading = forceLoadingParam === '1' || forceLoadingParam === 'true';
  const forceInvalidParam = String((urlParams && urlParams.get('invalid')) || '').toLowerCase();
  const forceCompletedParam = String((urlParams && urlParams.get('completed')) || '').toLowerCase();
  const forceInvalid = forceInvalidParam === '1' || forceInvalidParam === 'true';
  const forceCompleted = forceCompletedParam === '1' || forceCompletedParam === 'true';

  // Ensure loading is shown for at least 2 seconds, then fade out
  const [minLoadingElapsed, setMinLoadingElapsed] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  const [loadingLeaving, setLoadingLeaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMinLoadingElapsed(true), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const hasToken = Boolean(token);
    const wantsLoading = (hasToken ? apLoading : false) || forceLoading;
    if (wantsLoading) {
      if (!showLoading) setShowLoading(true);
      if (loadingLeaving) setLoadingLeaving(false);
    } else if (minLoadingElapsed) {
      if (showLoading && !loadingLeaving) {
        setLoadingLeaving(true);
      }
    }
  }, [apLoading, forceLoading, minLoadingElapsed, showLoading, loadingLeaving, token]);

  // Drive the fade-out once when leaving starts; avoid cancelling on re-renders
  useEffect(() => {
    if (loadingLeaving && !loadingHideTimerRef.current) {
      loadingHideTimerRef.current = setTimeout(() => {
        setShowLoading(false);
        setLoadingLeaving(false);
        loadingHideTimerRef.current = null;
      }, 320);
    }
  }, [loadingLeaving]);

  useEffect(() => () => {
    if (loadingHideTimerRef.current) {
      clearTimeout(loadingHideTimerRef.current);
      loadingHideTimerRef.current = null;
    }
  }, []);

  // (Removed) duplicate debug flags and early returns to avoid hook-order and re-declaration issues

  // Localized copy for buttons and short labels
  const copy = {
    EN: {
      back: 'Back',
      goBackAndEdit: 'Go Back and Edit',
      continueToActionPlanning: 'Continue to Action Planning',
      confirmAction: 'Confirm Action',
      reviewAndConfirm: 'Review & Confirm',
      submitPlan: 'Submit Plan',
      cancel: 'Cancel',
      yesSubmit: 'Yes, Submit',
      proceed: 'Proceed',
      close: 'Close',
      send: 'Send',
      validationRequired: 'Please complete Title, Description, and Timeframe before confirming.',
      acknowledgeRequired: 'Please check "I have read my report" before continuing.',
    },
    FR: {
      back: 'Retour',
      goBackAndEdit: 'Retour et modifier',
      continueToActionPlanning: "Continuer vers le plan d’action",
      confirmAction: "Confirmer l’action",
      reviewAndConfirm: 'Revoir et confirmer',
      submitPlan: 'Soumettre le plan',
      cancel: 'Annuler',
      yesSubmit: 'Oui, soumettre',
      proceed: 'Continuer',
      close: 'Fermer',
      send: 'Envoyer',
      validationRequired: 'Veuillez compléter le titre, la description et l’échéance avant de confirmer.',
      acknowledgeRequired: 'Veuillez cocher « J’ai lu mon rapport » avant de continuer.',
    },
    ES: {
      back: 'Atrás',
      goBackAndEdit: 'Volver y editar',
      continueToActionPlanning: 'Continuar a la planificación de acciones',
      confirmAction: 'Confirmar acción',
      reviewAndConfirm: 'Revisar y confirmar',
      submitPlan: 'Enviar plan',
      cancel: 'Cancelar',
      yesSubmit: 'Sí, enviar',
      proceed: 'Continuar',
      close: 'Cerrar',
      send: 'Enviar',
      validationRequired: 'Complete Título, Descripción y Plazo antes de confirmar.',
      acknowledgeRequired: 'Marca « He leído mi informe » antes de continuar.',
    },
  };
  const t = copy[language] || copy.EN;

  const reviewedCount = actions.filter(a => a.reviewed).length;
  const allReviewed = reviewedCount === actions.length;
  const isReviewMode = currentIndex >= questions.length;

  const planString = useMemo(() => {
    try {
      return JSON.stringify(planData);
    } catch (e) {
      return "";
    }
  }, []);

  function updateDate(id, date) {
    setActions((prev) => prev.map((a) => a.id === id ? { ...a, date } : a));
  }

  function toggleReviewed(id) {
    setActions((prev) => prev.map((a) => a.id === id ? { ...a, reviewed: !a.reviewed } : a));
  }

  function updateTitle(id, title) {
    setActions((prev) => prev.map((a) => a.id === id ? { ...a, title } : a));
  }

  function updateDescription(id, description) {
    setActions((prev) => prev.map((a) => a.id === id ? { ...a, description } : a));
  }

  function updateOwner(id, owner) {
    setActions((prev) => prev.map((a) => a.id === id ? { ...a, owner } : a));
  }

  function handleNextClick() {
    if (!introAcknowledged) {
      setValidationWarning(t.acknowledgeRequired);
      setTimeout(() => setValidationWarning(""), 2600);
      return;
    }
    // Intro -> Self-guided review mode directly
    setMode('self');
    setShowChatBar(false);
    // Initialize blank actions for self-guided flow using required count from API (fallback 3)
    const blank = createEmptyRows(apRequiredActionsCount);
    // Only initialize blanks on first entry. If actions already exist (from prior progress
    // or a previous self-guided session), keep them intact when returning from Intro.
    setActions((prev) => {
      const isSelfGuidedInitialized = prev.length === blank.length && prev.every((a, i) => a.id === `s${i + 1}`);
      const isDefaultDemo = prev.length === 3 && prev.every((a, i) => a.id === `a${i + 1}`);
      const hasAnyUserData = prev.some((a) =>
        (a.title && a.title.trim()) ||
        (a.description && a.description.trim()) ||
        a.date ||
        a.owner ||
        a.reviewed
      );
      if (isSelfGuidedInitialized || hasAnyUserData) return prev;
      // Replace the default demo actions on first continue, otherwise keep existing
      return isDefaultDemo ? blank : prev.length ? prev : blank;
    });
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      setCurrentIndex(questions.length); // jump to review mode on first action
    }, 350);
  }

  function handleBackClick() {
    if (isReviewMode) {
      // In self-guided mode: go back through actions, then back to intro
      const actionIndex = currentIndex - questions.length;
      if (actionIndex > 0) {
        setIsFadingOut(true);
        setTimeout(() => {
          setIsFadingOut(false);
          setCurrentIndex((prev) => prev - 1);
        }, 350);
      } else {
        setIsFadingOut(true);
        setTimeout(() => {
          setIsFadingOut(false);
          setCurrentIndex(-2);
        }, 350);
      }
      return;
    }
    if (currentIndex <= -2) return;
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      setCurrentIndex((prev) => {
        const nextIndex = prev <= -1 ? -2 : prev - 1;
        if (nextIndex < 0) {
          setShowChatBar(false);
          setShrinkNext(false);
        }
        return nextIndex;
      });
    }, 350);
  }

  function handleSend() {
    const value = chatDraft.trim();
    if (!value) return;
    setResponses((prev) => ({ ...prev, [currentIndex]: value }));
    setChatDraft("");

    const isLast = currentIndex === questions.length - 1;
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      if (isLast) {
        setChatIsLeaving(true);
        setTimeout(() => {
          setShowChatBar(false);
          setChatIsLeaving(false);
          setShowFinishBtn(true);
        }, 240);
        setCurrentIndex(questions.length); // enter review mode at first action
      } else {
        setCurrentIndex((prev) => prev + 1);
      }
    }, 350);
  }

  function handleConfirmActionNext() {
    const actionIndex = currentIndex - questions.length;
    if (actionIndex >= 0 && actionIndex < actions.length) {
      setActions((prev) => prev.map((a, i) => i === actionIndex ? { ...a, reviewed: true } : a));
    }
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      setCurrentIndex((prev) => prev + 1);
    }, 320);
  }

  function openNora() {
    setNoraOpen(true);
  }

  function closeNora() {
    setNoraOpen(false);
  }

  function sendNora() {
    const value = noraDraft.trim();
    if (!value) return;
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: value, timestamp: new Date() };
    setNoraMessages((prev) => [...prev, userMsg]);
    setNoraDraft("");
    // Stub assistant reply
    setIsStreaming(true);
    setTimeout(() => {
      const reply = { id: `a-${Date.now()}`, role: 'assistant', content: "Thanks — here’s a suggestion: try starting with an action verb, add a clear outcome, and include a target date. For example: ‘Define top 3 priorities for next week and share with the team by Friday.’", timestamp: new Date() };
      setNoraMessages((prev) => [...prev, reply]);
      setIsStreaming(false);
    }, 700);
  }

  const aiRef = React.useRef(null);

  const currentActionIndex = isReviewMode
    ? Math.min(actions.length - 1, Math.max(0, currentIndex - questions.length))
    : 0;
  const currentAction = actions[currentActionIndex] || actions[0];

  function extractJsonObject(text) {
    let raw = String(text || "").trim();
    const start = raw.indexOf("{");
    const end = raw.lastIndexOf("}");
    if (start !== -1 && end !== -1 && end > start) {
      raw = raw.slice(start, end + 1);
    }
    return JSON.parse(raw);
  }

  function buildSuggestionPrompt({ actionId }) {
    const existingActions = actions.map((a) => ({
      title: a.title || "",
      description: a.description || "",
    }));
    const prevRecs = (actionId && recommendationsByActionId[actionId]) || [];
    const avoidList = prevRecs.length
      ? `Previously suggested (avoid duplicates): ${prevRecs.map((p, i) => `#${i+1} title="${(p.title||'').replace(/"/g, '\\"')}", description="${(p.description||'').replace(/"/g, '\\"')}"`).join("; ")}.`
      : "";

    const languageName = language === 'FR' ? 'French' : (language === 'ES' ? 'Spanish' : 'English');

    return `You are an experienced organizational effectiveness consultant and action-planning assistant.

Context: Here is the full report/plan JSON to use for evidence, priorities, tone, and common factors (do not repeat or summarize it verbatim):
---
${planString}
---

Existing plan actions (titles and descriptions). Use these to avoid duplicates and to align scope and quality:
${JSON.stringify(existingActions)}

${avoidList}

Task: Propose exactly ONE high-quality, manager-friendly action with a short title and a clear, specific description. The description must be at least two sentences. Make it SMART when possible, feasible within 4–8 weeks, and focused on a single problem.

Evidence and grounding requirements for the explanation:
- Base every claim on concrete items from the report JSON (e.g., factor names, scores, deltas/trends, benchmarks, priority tags, risks, recurring blockers, verbatim themes).
- In each of the three sections below, include at least one explicit reference to the report using exact labels/values. Use concise inline citations like: (Factor: Trust, score 62, −5 vs benchmark 70) or a short quote: "…". If a data point is not available, say it is not available; never invent numbers.
- Prefer linking the action to top priorities: lowest-scoring factors, negative trends, high-impact drivers, or frequent themes.
- Avoid duplicating any existing or previously suggested action; if similar, choose a different angle, scope, or audience.

Explanation structure (three sections, each 1–2 sentences):
1) What this addresses: State the core problem/need and cite specific evidence from the report.
2) Why this recommendation was selected: Explain the reasoning and alignment to priorities with explicit evidence (metrics, trends, or themes).
3) What impact it will have: Describe the expected outcome in practical terms, citing relevant KPIs or factors from the report when available.

Language: ${languageName}. Write the action title, description, and explanation strictly in ${languageName}. Do not include any English words when ${languageName} is not English.

Constraints:
- Title: max 8 words. Description: 2–3 sentences, specific and actionable. Explanation: 3–5 sentences total across the three sections, concise and non-jargony. Do not focus on timeframe, explain as best as possible.
- Do not repeat large chunks of the report. Do not add extra keys or any text outside the JSON object.

Output format: Return ONLY a strict JSON object with keys exactly as follows (no code fences, no extra text). All scores are in base 10 should be shown in % rounded. All string values must be in ${languageName}:
{"action":{"title":"...","description":"..."},"explanation":{"addresses":"...","whySelected":"...","impact":"..."}}`;
  }

  function requestSuggestion({ actionId }) {
    if (!actionId) return;
    const existing = recommendationsByActionId[actionId] || [];
    if (existing.length >= 2) {
      return;
    }
    setSuggestionLoading(true);
    // Pre-select the next tab (new recommendation slot) and open the drawer
    setExplanationOpenForId((id) => id || actionId);
    const nextIndex = existing.length; // 0-based
    setSelectedRecommendationIndexByActionId((prev) => ({ ...prev, [actionId]: nextIndex }));
    // Clear any previous error for this action before retrying
    setErrorByActionId((prev) => ({ ...prev, [actionId]: null }));

    // Simulate generation delay and result selection from recommendations.json
    const baseIndex = currentActionIndex * 2; // action 0 -> 0,1 ; action 1 -> 2,3 ; action 2 -> 4,5
    const recIndex = baseIndex + nextIndex;
    // Guard against out-of-range
    const picked = mockRecommendations[recIndex] || null;

    // Leave old dispatch code commented out per request
    // dispatch({ type: CLEAR_CHAT_GPT_SUMMARY });
    // tokenRef.current = uuidv4();
    // dispatch({
    //   type: SG_GET_CHAT_GPT_SUMMARY,
    //   payload: {
    //     payload: buildSuggestionPrompt({ actionId }),
    //     token: tokenRef.current,
    //   },
    // });

    setTimeout(() => {
      if (picked && currentAction?.id) {
        let nextLength = 0;
        const suggested = picked.action || {};
        const structured = picked.explanation || null;
        setRecommendationsByActionId((prev) => {
          const prevArr = prev[currentAction.id] || [];
          if (prevArr.length >= 2) return prev; // guard
          const explanation = structured ? {
            addresses: String(structured.addresses || ''),
            whySelected: String(structured.whySelected || ''),
            impact: String(structured.impact || ''),
          } : { addresses: '', whySelected: '', impact: '' };
          const nextArr = [...prevArr, { title: String(suggested.title || ""), description: String(suggested.description || ""), explanation }];
          nextLength = nextArr.length;
          return { ...prev, [currentAction.id]: nextArr };
        });
        setSelectedRecommendationIndexByActionId((prev) => ({ ...prev, [currentAction.id]: (prev[currentAction.id] ?? ((recommendationsByActionId[currentAction.id] || []).length)) }));
        setExplanationOpenForId((id) => id || currentAction.id);
        setRevealTokenByActionId((prev) => ({ ...prev, [currentAction.id]: `${currentAction.id}-${Date.now()}-${Math.max(1, nextLength)}` }));
        setErrorByActionId((prev) => ({ ...prev, [currentAction.id]: null }));
      }
      setSuggestionLoading(false);
    }, 2000);
  }

  function handleSuggestAction() {
    if (!currentAction?.id) return;
    const actionId = currentAction.id;
    const recs = recommendationsByActionId[actionId] || [];
    // Ensure two recommendations are present immediately
    if (recs.length < 2) {
      const baseIndex = currentActionIndex * 2;
      const needed = 2 - recs.length;
      const toAdd = [];
      for (let i = 0; i < needed; i++) {
        const picked = mockRecommendations[baseIndex + recs.length + i] || null;
        if (picked) {
          const suggested = picked.action || {};
          const structured = picked.explanation || null;
          const explanation = structured ? {
            addresses: String(structured.addresses || ''),
            whySelected: String(structured.whySelected || ''),
            impact: String(structured.impact || ''),
          } : { addresses: '', whySelected: '', impact: '' };
          toAdd.push({ title: String(suggested.title || ''), description: String(suggested.description || ''), explanation });
        }
      }
      if (toAdd.length) {
        setRecommendationsByActionId((prev) => {
          const prevArr = prev[actionId] || [];
          const nextArr = [...prevArr, ...toAdd].slice(0, 2);
          return { ...prev, [actionId]: nextArr };
        });
      }
    }
    setExplanationOpenForId(actionId);
    setSelectedRecommendationIndexByActionId((prev) => {
      const nextSelSource = recommendationsByActionId[actionId] || [];
      const existingSel = prev[actionId];
      const isValid = typeof existingSel === 'number' && existingSel >= 0 && existingSel < Math.max(1, nextSelSource.length);
      const nextSel = isValid ? existingSel : Math.max(0, (nextSelSource.length || 1) - 1);
      return { ...prev, [actionId]: nextSel };
    });
  }

  function handleOpenExplanation() {
    if (!currentAction?.id) return;
    const actionId = currentAction.id;
    // Ensure two recommendations are present immediately
    const recs = recommendationsByActionId[actionId] || [];
    if (recs.length < 2) {
      const baseIndex = currentActionIndex * 2;
      const needed = 2 - recs.length;
      const toAdd = [];
      for (let i = 0; i < needed; i++) {
        const picked = mockRecommendations[baseIndex + recs.length + i] || null;
        if (picked) {
          const suggested = picked.action || {};
          const structured = picked.explanation || null;
          const explanation = structured ? {
            addresses: String(structured.addresses || ''),
            whySelected: String(structured.whySelected || ''),
            impact: String(structured.impact || ''),
          } : { addresses: '', whySelected: '', impact: '' };
          toAdd.push({ title: String(suggested.title || ''), description: String(suggested.description || ''), explanation });
        }
      }
      if (toAdd.length) {
        setRecommendationsByActionId((prev) => {
          const prevArr = prev[actionId] || [];
          const nextArr = [...prevArr, ...toAdd].slice(0, 2);
          return { ...prev, [actionId]: nextArr };
        });
      }
    }
    setExplanationOpenForId(actionId);
    setSelectedRecommendationIndexByActionId((prev) => {
      const nextSelSource = recommendationsByActionId[actionId] || [];
      const existingSel = prev[actionId];
      const isValid = typeof existingSel === 'number' && existingSel >= 0 && existingSel < Math.max(1, nextSelSource.length);
      const nextSel = isValid ? existingSel : Math.max(0, (nextSelSource.length || 1) - 1);
      return { ...prev, [actionId]: nextSel };
    });
  }

  function handleCloseExplanation() {
    setExplanationOpenForId(null);
  }

  useEffect(() => {
    if (!suggestionLoading) return;
    const summary = chatGptState?.chat_gpt_summary;
    if (!summary) return;
    const responseText = summary?.response;
    // Handle error payloads or transport failures (response may be null)
    const isErrorString = typeof responseText === 'string' && responseText.startsWith('ERROR:');
    const isTransportError = typeof summary?.status === 'number' && summary.status >= 400;
    const hasErrorField = Boolean(summary?.error);
    const isError = (!responseText && (isTransportError || hasErrorField)) || isErrorString;
    if (isError) {
      if (currentAction?.id) {
        const errMsg = summary?.message || summary?.error || 'Unable to generate a recommendation. Please try again.';
        setErrorByActionId((prev) => ({ ...prev, [currentAction.id]: String(errMsg) }));
        setExplanationOpenForId((id) => id || currentAction.id);
      }
      setSuggestionLoading(false);
      return;
    }
    if (!responseText) return;
    try {
      const parsed = extractJsonObject(responseText);
      const suggested = parsed?.action || {};
      const structured = parsed?.explanation && typeof parsed.explanation === 'object' ? parsed.explanation : null;
      if (suggested && (suggested.title || suggested.description) && currentAction?.id) {
        let nextLength = 0;
        setRecommendationsByActionId((prev) => {
          const prevArr = prev[currentAction.id] || [];
          if (prevArr.length >= 2) return prev; // guard
          const explanation = structured ? {
            addresses: String(structured.addresses || ''),
            whySelected: String(structured.whySelected || ''),
            impact: String(structured.impact || ''),
          } : { addresses: '', whySelected: '', impact: '' };
          const nextArr = [...prevArr, { title: String(suggested.title || ""), description: String(suggested.description || ""), explanation }];
          nextLength = nextArr.length;
          return { ...prev, [currentAction.id]: nextArr };
        });
        setSelectedRecommendationIndexByActionId((prev) => ({ ...prev, [currentAction.id]: (prev[currentAction.id] ?? ((recommendationsByActionId[currentAction.id] || []).length)) }));
        setExplanationOpenForId((id) => id || currentAction.id);
        setRevealTokenByActionId((prev) => ({ ...prev, [currentAction.id]: `${currentAction.id}-${Date.now()}-${Math.max(1, nextLength)}` }));
        setErrorByActionId((prev) => ({ ...prev, [currentAction.id]: null }));
      }
    } catch (e) {
      // Record parse or transport errors for graceful UI
      if (currentAction?.id) {
        const raw = chatGptState?.chat_gpt_summary;
        const errMsg = raw?.message || raw?.error || 'Unable to generate a recommendation. Please try again.';
        setErrorByActionId((prev) => ({ ...prev, [currentAction.id]: String(errMsg) }));
        setExplanationOpenForId((id) => id || currentAction.id);
      }
    } finally {
      setSuggestionLoading(false);
    }
  }, [chatGptState, suggestionLoading, currentActionIndex, currentAction?.id]);

  // Removed ModeSelect and AI path; default is self-guided

  function handleProceedToFinalReview() {
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      setShowFinalReview(true);
    }, 320);
  }

  function handleEditFromFinalReview(actionIndex) {
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      setShowFinalReview(false);
      setCurrentIndex(questions.length + actionIndex);
    }, 200);
  }

  function handleSubmitFinalize() {
    try {
      localStorage.setItem("actionPlan:v1", JSON.stringify({ actions, submitted: true, submittedAt: Date.now() }));
    } catch (e) {}
    // If token + active planning event exist, submit via token flow (PATCH event response)
    if (token && apActiveEvent?.id) {
      const response_data = {
        actions: actions.map((a) => ({
          id: a.id,
          title: a.title || "",
          description: a.description || "",
          time_bound_deadline: a.date || "",
          owner_manager: a.owner || "",
        })),
      };
      try {
        // eslint-disable-next-line no-console
        console.log('[AP] Submitting action plan via token', { eventId: apActiveEvent.id, actions: response_data.actions.length });
        dispatch({ type: 'SG_AP_SUBMIT', payload: { eventId: apActiveEvent.id, token, response_data } });
      } catch (e) {}
    }
    setShowSubmitConfirm(false);
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      setShowSubmissionComplete(true);
    }, 350);
  }

  function handleSubmitWithConfirm() {
    if (!finalConfirmed) return;
    setShowSubmitConfirm(true);
  }

  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [validationWarning, setValidationWarning] = useState("");

  // AI back confirm removed in self-guided only flow

  const canConfirmCurrent = isReviewMode && Boolean(currentAction?.title?.trim() && currentAction?.description?.trim() && currentAction?.date);

  function handleAttemptConfirmAction() {
    if (!canConfirmCurrent) {
      setValidationWarning(t.validationRequired);
      setTimeout(() => setValidationWarning(""), 2600);
      return;
    }
    handleConfirmActionNext();
  }
  // Splash states (after hooks to keep consistent hook order)
  if (showLoading) {
    // eslint-disable-next-line no-console
    console.debug('[AP] Loading…', { apLoading, minLoadingElapsed, loadingLeaving });
    return <Loading themeColors={themeColors} leaving={loadingLeaving} />;
  }
  if (forceInvalid) {
    // eslint-disable-next-line no-console
    console.debug('[AP] Invalid token screen forced');
    return <InvalidToken themeColors={themeColors} />;
  }
  if (apInvalidToken) {
    // eslint-disable-next-line no-console
    console.debug('[AP] Invalid token detected from API');
    return <InvalidToken themeColors={themeColors} />;
  }

  // eslint-disable-next-line no-console
  console.debug('[AP] Active event snapshot', apActiveEvent);
  // Show Completed screen if API reports a completed response
  const statusFromApi = String(apActiveEvent?.response_status || apActiveEvent?.status || '').toLowerCase();
  const isCompletedFromApi = statusFromApi === 'completed' || statusFromApi === 'closed' || statusFromApi === 'done';
  const zeroRequiredActions = Number(apActiveEvent?.required_actions_count) === 0;
  const looksCompleted = isCompletedFromApi || (zeroRequiredActions && Array.isArray(apActions) && apActions.length > 0);
  if (forceCompleted || looksCompleted) {
    // eslint-disable-next-line no-console
    console.debug('[AP] Completed screen', { forceCompleted, statusFromApi, zeroRequiredActions, actionsLen: (apActions||[]).length });
    return <Completed themeColors={themeColors} />;
  }

  return (
    <Container themeColors={themeColors} style={{ fontFamily: 'Geist Variable, sans-serif' }}>
      <TopBanner themeColors={themeColors}>
        <Logo src={LogoImage} alt="Logo" />
        <Language language={language} setLanguage={setLanguage} languages={["ES"]} />
      </TopBanner>

      <BackgroundLayer>
        <BlobLeft />
        <BlobRight />
      </BackgroundLayer>

      <Foreground>
        <Content>
          <CenteredBlock>
            <AnimatedCard isFadingOut={isFadingOut}>
              {showSubmissionComplete ? (
                <SubmissionComplete />
              ) : showPreSubmit ? (
                <PreSubmitInfo language={language} />
              ) : showFinalReview ? (
                <ReviewConfirm actions={actions} />
              ) : !isReviewMode ? (
                currentIndex <= -1 ? (
                  <Introduction
                    themeColors={themeColors}
                    reportUrl={sampleJson?.themeSettings?.reportUrl}
                    language={language}
                    acknowledged={introAcknowledged}
                    onAcknowledgeChange={setIntroAcknowledged}
                  />
                ) : (
                  // Defensive fallback: if somehow not in review mode and no intro, show intro
                  <Introduction
                    themeColors={themeColors}
                    reportUrl={sampleJson?.themeSettings?.reportUrl}
                    language={language}
                    acknowledged={introAcknowledged}
                    onAcknowledgeChange={setIntroAcknowledged}
                  />
                )
              ) : (
                <>
                  <PlanHeader>
                    <EpicTitle>{language === 'FR' ? 'Mes actions' : (language === 'ES' ? 'Mis acciones' : 'My Actions')}</EpicTitle>
                    <SubTitle>{language === 'FR' ? 'Passez en revue chaque action, modifiez si nécessaire, puis confirmez.' : (language === 'ES' ? 'Revisa cada acción, edita si es necesario y confirma.' : 'Set actions yourself, or use evidence-based AI recommendations based on your report results.')}</SubTitle>
                    {apLoadingBootstrap && <div style={{ color: "#6b7280", fontSize: 13 }}>Loading your assigned actions…</div>}
                    {apState?.submitting && <div style={{ color: "#6b7280", fontSize: 13 }}>Submitting your action plan…</div>}
                    {apError && !apLoading && <div style={{ color: "#b91c1c", fontSize: 13 }}>Failed to load server data. You can still create actions locally.</div>}
                  </PlanHeader>
                  {apActiveEvent && (
                    <div style={{ color: "#6b7280", fontSize: 13 }}>
                      Event: {apActiveEvent.name || apActiveEvent.id} ({apActiveEvent.event_type})
                    </div>
                  )}
                  <ActionStep
                    themeColors={themeColors}
                    isFadingOut={isFadingOut}
                    action={currentAction}
                    index={currentActionIndex}
                    total={actions.length}
                    onChangeTitle={updateTitle}
                    onChangeDescription={updateDescription}
                    onChangeDate={updateDate}
                    onConfirm={toggleReviewed}
                    onSuggestAction={handleSuggestAction}
                    suggestionLoading={suggestionLoading}
                    hasExplanation={Boolean(currentAction?.id && (recommendationsByActionId[currentAction.id] || []).length > 0)}
                    onOpenExplanation={handleOpenExplanation}
                    language={language}
                  />
                </>
              )}
            </AnimatedCard>
          </CenteredBlock>
        </Content>
      </Foreground>

      <BottomBar>
        <ButtonWrapper>
          {(currentIndex >= -2 || isReviewMode || showFinalReview || showPreSubmit) && !showSubmissionComplete ? (
            <BackButton onClick={() => {
              if (showPreSubmit) {
                setIsFadingOut(true);
                setTimeout(() => {
                  setIsFadingOut(false);
                  setShowPreSubmit(false);
                  setShowFinalReview(true);
                }, 320);
                return;
              }
              if (showFinalReview) {
                setIsFadingOut(true);
                setTimeout(() => {
                  setIsFadingOut(false);
                  setShowFinalReview(false);
                  setCurrentIndex(questions.length + actions.length - 1);
                }, 320);
                return;
              }
              handleBackClick();
            }} themeColors={themeColors}>{showFinalReview ? t.goBackAndEdit : t.back}</BackButton>
          ) : (
            <div style={{ width: "100px" }} />
          )}
        </ButtonWrapper>

        <ProgressWrapper>
          {!isReviewMode ? (
            currentIndex >= 0 ? (
              <Progress>
                Question {currentIndex + 1} of {questions.length}
              </Progress>
            ) : (
              <div style={{ height: 18 }} />
            )
          ) : (
            <div style={{ height: 18 }} />
          )}
        </ProgressWrapper>

        <ButtonWrapper>
          {showSubmissionComplete ? (
            <div style={{ width: 100 }} />
          ) : showPreSubmit && !showSubmissionComplete ? (
            <>
              <GrowButtonWrapper isGrowing={true}>
                <NextButton onClick={() => setShowSubmitConfirm(true)} active={true} themeColors={themeColors}>{t.submitPlan}</NextButton>
              </GrowButtonWrapper>
            </>
          ) : showFinalReview && !showSubmissionComplete ? (
            <>
              <GrowButtonWrapper isGrowing={true}>
                <NextButton onClick={() => { setIsFadingOut(true); setTimeout(() => { setIsFadingOut(false); setShowFinalReview(false); setShowPreSubmit(true); }, 280); }} active={true} themeColors={themeColors}>{t.proceed}</NextButton>
              </GrowButtonWrapper>
            </>
          ) : (
            !isReviewMode ? (
              currentIndex === -2 ? (
                <NextButtonWrapper isShrinking={shrinkNext}>
                  <NextButton
                    onClick={handleNextClick}
                    active={introAcknowledged && (!token || !apLoadingBootstrap)}
                    themeColors={themeColors}
                  >
                    {t.continueToActionPlanning}
                  </NextButton>
                </NextButtonWrapper>
              ) : (
                <div style={{ width: 100 }} />
              )
            ) : (
              <>
                {currentIndex < questions.length + actions.length - 1 ? (
                  <NextButton onClick={handleAttemptConfirmAction} active={canConfirmCurrent} themeColors={themeColors}>{t.confirmAction}</NextButton>
                ) : (
                  <GrowButtonWrapper isGrowing={true}>
                    <NextButton onClick={handleProceedToFinalReview} active={allReviewed} themeColors={themeColors}>{t.reviewAndConfirm}</NextButton>
                  </GrowButtonWrapper>
                )}
              </>
            )
          )}
        </ButtonWrapper>
      </BottomBar>

      {Boolean(validationWarning) && (
        <div role="alert" aria-live="assertive" style={{ position: 'fixed', left: '50%', bottom: 92, transform: 'translateX(-50%)', background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderRadius: 10, padding: '10px 14px', fontSize: 14, zIndex: 2200 }}>
          {validationWarning}
        </div>
      )}

      {/* Chat bar disabled in self-guided direct flow */}

      {showSubmitConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 12px 32px rgba(0,0,0,0.12)', padding: 16 }}>
            <div style={{ fontWeight: 600, color: 'oklch(.145 0 0)', fontSize: 16 }}>Submit actions?</div>
            <p style={{ marginTop: 6, color: '#4b5563', fontSize: 14 }}>Are you sure you want to submit your actions now? You can still go back and edit if you hit cancel.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
              <button onClick={() => setShowSubmitConfirm(false)} style={{ height: 40, padding: '0 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#111827', fontWeight: 600, cursor: 'pointer' }}>{t.cancel}</button>
              <button onClick={handleSubmitFinalize} style={{ height: 40, padding: '0 14px', borderRadius: 8, border: 'none', background: themeColors.buttonColor, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{t.yesSubmit}</button>
            </div>
          </div>
        </div>
      )}

      {showAIGoBackConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
          <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 12px 32px rgba(0,0,0,0.12)', padding: 16 }}>
            <div style={{ fontWeight: 600, color: 'oklch(.145 0 0)', fontSize: 16 }}>Restart AI guided process?</div>
            <p style={{ marginTop: 6, color: '#4b5563', fontSize: 14 }}>Going back will restart the AI process and return you to the selection screen.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
              <button onClick={() => setShowAIGoBackConfirm(false)} style={{ height: 40, padding: '0 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#111827', fontWeight: 600, cursor: 'pointer' }}>{t.cancel}</button>
              <button onClick={confirmAIGoBackToSelection} style={{ height: 40, padding: '0 14px', borderRadius: 8, border: 'none', background: themeColors.buttonColor, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{t.proceed}</button>
            </div>
          </div>
        </div>
      )}

      {/* Recommendation Drawer */}
      {Boolean(explanationOpenForId) && <NoraOverlay onClick={handleCloseExplanation} />}
      <NoraDrawer isOpen={Boolean(explanationOpenForId)}>
        <NoraHeader>
          <NoraHeaderLeft>
            <NoraTitle>{language === 'FR' ? 'Recommandation IA' : (language === 'ES' ? 'Recomendación IA' : 'AI Recommendation')}</NoraTitle>
            <NoraSubtitle>{language === 'FR' ? "Générez jusqu’à 2 recommandations et choisissez celle à appliquer" : (language === 'ES' ? 'Genera hasta 2 recomendaciones y elige cuál aplicar' : 'Based on your report, we have generated 2 recommended actions, choose one to apply.')}</NoraSubtitle>
          </NoraHeaderLeft>
          <NoraClose onClick={handleCloseExplanation} aria-label="Close">✕</NoraClose>
        </NoraHeader>
        <NoraMessages>
          {(() => {
            const aid = explanationOpenForId;
            const recs = aid ? (recommendationsByActionId[aid] || []) : [];
            const hasError = aid ? Boolean(errorByActionId[aid]) : false;
            const rawSel = aid ? (selectedRecommendationIndexByActionId[aid] ?? 0) : 0;
            const displayCount = recs.length;
            const sel = Math.max(0, Math.min(rawSel, Math.max(0, displayCount - 1)));
            const current = recs[sel] || null;
            return (
              <div>
                {/* Tabs */}
                {displayCount > 1 && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    {[...Array(displayCount)].map((_, i) => (
                      <button
                        key={`tab-${i}`}
                        onClick={() => setSelectedRecommendationIndexByActionId((prev) => ({ ...prev, [aid]: i }))}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 9999,
                          border: i === sel ? '2px solid #111827' : '1px solid #e5e7eb',
                          background: i === sel ? '#111827' : '#fff',
                          color: i === sel ? '#fff' : '#111827',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {(language === 'FR' ? 'Recommandation' : (language === 'ES' ? 'Recomendación' : 'Recommendation'))} {i + 1}
                      </button>
                    ))}
                  </div>
                )}

                {current ? (
                  <RecommendationReveal key={revealTokenByActionId[aid]}>
                    {errorByActionId[aid] && !suggestionLoading && (
                      <div style={{ border: '1px solid #fef08a', background: '#fffbeb', color: '#713f12', padding: 10, borderRadius: 8, marginBottom: 12 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          {language === 'FR' ? 'La dernière génération a échoué' : (language === 'ES' ? 'La última generación falló' : 'The last generation failed')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <div style={{ color: '#92400e' }}>
                            {language === 'FR' ? 'Affichage de la recommandation précédente.' : (language === 'ES' ? 'Mostrando la recomendación anterior.' : 'Showing the previous recommendation.')}
                          </div>
                          <button
                            onClick={() => { if (aid) requestSuggestion({ actionId: aid }); }}
                            style={{ height: 32, padding: '0 12px', borderRadius: 8, border: 'none', background: '#111827', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                          >
                            {language === 'FR' ? 'Réessayer' : (language === 'ES' ? 'Reintentar' : 'Retry')}
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Loading removed as recommendations are available immediately */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, color: 'oklch(.145 0 0)', fontFamily: 'Lora, serif' }}>{current.title}</div>
                      <div style={{ marginTop: 6, color: '#374151', fontFamily: 'Lora, serif' }}>{current.description}</div>
                    </div>

                    {/* Structured explanation */}
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontWeight: 700, color: '#111827' }}>{language === 'FR' ? 'Ce que cela adresse' : (language === 'ES' ? 'Qué aborda' : 'What this addresses')}</div>
                        <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'Lora, serif', color: '#374151' }}>{current.explanation?.addresses || ''}</div>
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontWeight: 700, color: '#111827' }}>{language === 'FR' ? 'Pourquoi sélectionné' : (language === 'ES' ? 'Por qué se seleccionó' : 'Why this recommendation was selected')}</div>
                        <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'Lora, serif', color: '#374151' }}>{current.explanation?.whySelected || ''}</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: '#111827' }}>{language === 'FR' ? 'Impact attendu' : (language === 'ES' ? 'Impacto esperado' : 'What impact will it have')}</div>
                        <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'Lora, serif', color: '#374151' }}>{current.explanation?.impact || ''}</div>
                      </div>
                    </div>
                  </RecommendationReveal>
                ) : (
                  <div>
                    {hasError ? (
                      <div style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#7f1d1d', padding: 12, borderRadius: 10 }}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>
                          {language === 'FR' ? 'Échec de la génération' : (language === 'ES' ? 'Fallo en la generación' : 'Generation failed')}
                        </div>
                        <div style={{ marginBottom: 4, color: '#7f1d1d' }}>
                          {language === 'FR' ? "Nous n'avons pas pu générer une recommandation pour le moment." : (language === 'ES' ? 'No pudimos generar una recomendación en este momento.' : "We couldn't generate a recommendation right now.")}
                        </div>
                        <div style={{ marginBottom: 12, color: '#991b1b' }}>
                          {language === 'FR' ? 'Veuillez réessayer. Si le problème persiste, réessayez plus tard.' : (language === 'ES' ? 'Inténtalo de nuevo. Si continúa, inténtalo más tarde.' : 'Please try again. If this keeps happening, try again later.')}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => { if (aid) requestSuggestion({ actionId: aid }); }}
                            style={{ height: 40, padding: '0 14px', borderRadius: 10, border: 'none', background: '#111827', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                          >
                            {language === 'FR' ? 'Réessayer' : (language === 'ES' ? 'Reintentar' : 'Retry')}
                          </button>
                          <button
                            onClick={handleCloseExplanation}
                            style={{ height: 40, padding: '0 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#111827', fontWeight: 600, cursor: 'pointer' }}
                          >
                            {t.close}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#6b7280' }}>
                        {language === 'FR' ? 'Aucune recommandation à afficher.' : (language === 'ES' ? 'No hay recomendaciones para mostrar.' : 'No recommendation to display.')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </NoraMessages>
        <div style={{ padding: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {(() => {
            const aid = explanationOpenForId;
            const recs = aid ? (recommendationsByActionId[aid] || []) : [];
            return (
              <>
                <button
                  onClick={() => {
                    if (!aid) return;
                    const sel = selectedRecommendationIndexByActionId[aid] ?? 0;
                    const chosen = recs[sel];
                    if (!chosen) return;
                    setActions((prev) => prev.map((a) => a.id === aid ? { ...a, title: chosen.title, description: chosen.description } : a));
                    handleCloseExplanation();
                  }}
                  style={{ height: 40, padding: '0 14px', borderRadius: 10, border: 'none', background: themeColors.buttonColor, color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                >
                  {language === 'FR' ? 'Utiliser cette recommandation' : (language === 'ES' ? 'Usar esta recomendación' : 'Use this recommendation')}
                </button>
                <NoraSendButton themeColors={themeColors} onClick={handleCloseExplanation}>{t.close}</NoraSendButton>
              </>
            );
          })()}
        </div>
      </NoraDrawer>

      {/* Nora Drawer (legacy chat) */}
      {noraOpen && <NoraOverlay onClick={closeNora} />}
      <NoraDrawer isOpen={noraOpen}>
        <NoraHeader>
          <NoraHeaderLeft>
            <NoraTitle>{language === 'FR' ? 'Demander à Nora' : (language === 'ES' ? 'Preguntar a Nora' : 'Ask Nora')}</NoraTitle>
            <NoraSubtitle>{language === 'FR' ? 'Nora est votre assistante pour vous aider à formuler des actions.' : (language === 'ES' ? 'Nora es tu asistente para ayudarte a formular acciones.' : 'Nora is your assistant to help you formulate actions.')}</NoraSubtitle>
          </NoraHeaderLeft>
          <NoraClose onClick={closeNora} aria-label="Close">✕</NoraClose>
        </NoraHeader>
        <NoraMessages id="nora-scroll">
          {noraMessages.map((m, idx) => (
            <div key={m.id} style={{ display: 'block' }}>
              <NoraMessageRow role={m.role}>
                <NoraMessageBubble role={m.role} themeColors={themeColors}>
                  {m.role === 'assistant' ? (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}{isStreaming && m.id === noraMessages[noraMessages.length - 1]?.id && <TypingDot />}</div>
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
                  )}
                </NoraMessageBubble>
              </NoraMessageRow>
              {(!isStreaming || m.id !== noraMessages[noraMessages.length - 1]?.id) && (
                <NoraTimestamp>{m.timestamp ? m.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</NoraTimestamp>
              )}
            </div>
          ))}
        </NoraMessages>
        <NoraInputBar>
          <NoraTextField
            value={noraDraft}
            onChange={(e) => setNoraDraft(e.target.value)}
            placeholder="Type a message..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendNora();
              }
            }}
          />
          <NoraSendButton themeColors={themeColors} onClick={sendNora}>{t.send}</NoraSendButton>
        </NoraInputBar>
      </NoraDrawer>
    </Container>
  );
}
